// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/growth-score/pages/BrandPage.tsx (ADAPT)
// ADAPTED (plan P4-1, Coverage "Brand page: real"): the profile is READ-ONLY and comes from our data, not fixtures: what the
// person confirmed in the chat (the browser session), else the brand guide the BFF read. Not ported: the product / age-band
// chips that re-rank the cohort, "Other…", the "% of Match" weights (no Match model here, the cohort is the BFF's) and the
// confirm button (the profile was confirmed in the chat). The logo is the one the brand's own site gave.
/**
 * Growth Score → Your brand: the profile the cohort was picked against, built from the site and our research.
 */
import { ArrowRight } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { Card } from '@/components/ui/shadcn/card'
import { selectionSurface } from '@/components/ui/selection'
import { listedProducts, profileTexts } from '@/features/onboarding-chat/components/BrandProfileCard'
import { COULDNT_READ } from '@/features/onboarding-chat/copy'
import { cn } from '@/lib/utils'
import { GrowthPage, useGrowthNav } from '../GrowthPage'
import { useGrowthScore } from '../GrowthScoreContext'
import { BrandMark } from '../components/visuals'

export function BrandPage() {
  const vm = useGrowthScore()
  const nav = useGrowthNav()
  const texts = vm.confirmedProfile ?? profileTexts(vm.guide)
  const products = vm.confirmedProfile?.products ?? listedProducts(vm.guide)
  return (
    <GrowthPage
      title="Your brand"
      back
      hero={{
        icon: <BrandMark name={vm.brand} logoUrl={vm.logoUrl} />,
        subtitle: `${vm.brand}${vm.site ? ` · built from ${vm.site}` : ''} and our research. It is what we picked your cohort against.`,
      }}
    >
      <Card role="group" aria-label="Your brand" className="flex flex-col gap-3 p-4">
        <div className="flex items-center gap-2.5">
          <BrandMark name={vm.brand} logoUrl={vm.logoUrl} size="sm" />
          <h3 className="text-heading-sm text-foreground">{vm.brand}</h3>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <Field label="Products">
            {products.length ? (
              <ul className="flex flex-wrap gap-1.5">
                {products.map((p) => (
                  <li
                    key={p}
                    className={cn(
                      'inline-flex min-h-7 max-w-full items-center border border-ds-hairline px-2.5 py-0.5 text-left text-tab',
                      selectionSurface({ level: 'card-tab', selected: true }),
                    )}
                  >
                    {p}
                  </li>
                ))}
              </ul>
            ) : (
              <Missing />
            )}
          </Field>
          <Field label="Customer">{texts.customer ? <p className="text-body-sm text-foreground">{texts.customer}</p> : <Missing />}</Field>
          <Field label="Positioning">{texts.positioning ? <p className="text-body-sm text-foreground">{texts.positioning}</p> : <Missing />}</Field>
          <Field label="Tone">{texts.tone ? <p className="text-body-sm text-foreground">{texts.tone}</p> : <Missing />}</Field>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => nav.open({ kind: 'assessment' })}>
            Back
          </Button>
          <Button type="button" onClick={() => nav.open({ kind: 'cohort' })}>
            See your comparable cohort
            <ArrowRight aria-hidden />
          </Button>
        </div>
      </Card>
    </GrowthPage>
  )
}

/** One part of the profile. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 rounded-md border border-ds-hairline p-3" role="group" aria-label={label}>
      <span className="text-caption text-ds-text-2">{label}</span>
      {children}
    </div>
  )
}

const Missing = () => <span className="text-body-sm text-ds-text-3">{COULDNT_READ}</span>
